import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import type {
  CreateCategoryRequest,
  UnassignedFilter,
  UpdateCategoryRequest,
} from '@/api/types'
import { categoriesApi } from '@/api/endpoints/categories'
import { getErrorMessage } from '@/shared/utils/error-codes'
import { ApiError } from '@/api/types'

/**
 * Query hook to list all categories
 */
export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
  })
}

/**
 * Mutation hook to create a new category
 */
export function useCreateCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCategoryRequest) => categoriesApi.create(data),
    onSuccess: () => {
      toast.success('Category created successfully')
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error('Failed to create category')
      }
    },
  })
}

/**
 * Rename, re-parent or reorder a category.
 *
 * The server owns the two-level rule and answers a refusal with a reason code; surfacing that code
 * rather than a generic message is the whole point, because the reasons differ in what the user has
 * to do next.
 */
export function useUpdateCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCategoryRequest }) =>
      categoriesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      queryClient.invalidateQueries({ queryKey: ['categories', 'unassigned'] })
    },
  })
}

export interface UnassignedQuery {
  from?: string
  to?: string
  q?: string
  filter?: UnassignedFilter
  page?: number
  pageSize?: number
}

/**
 * The assignment worklist.
 *
 * `keepPreviousData` on purpose: paging and typing in the search box would otherwise blank the
 * table on every keystroke, which reads as a broken page rather than as loading.
 */
export function useUnassignedProducts(params: UnassignedQuery) {
  return useQuery({
    queryKey: ['categories', 'unassigned', params],
    queryFn: () => categoriesApi.unassigned(params),
    placeholderData: keepPreviousData,
  })
}

/**
 * Assigns several articles to one category in a single call.
 *
 * The server applies them in one transaction — all or nothing — so there is no partial state to
 * reconcile here on failure.
 */
export function useAssignProducts() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, skus }: { id: number; skus: Array<string> }) =>
      categoriesApi.assign(id, skus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      queryClient.invalidateQueries({ queryKey: ['categories', 'unassigned'] })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error('Failed to assign products')
      }
    },
  })
}
